import { Badge } from '@/components/ui/badge';
import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';

/** Clu's one Slack message, to a channel or a member, confirmed on its card. */
export const SEND_SLACK_MESSAGE_ACTION = 'send_slack_message';

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The card's description without the message text the server appends after
 * its first line: the preview shows that text exactly, so the description
 * stops at where it goes. Any other card's description as it came.
 */
export function confirmationDescription(request: {
  tool_name?: string;
  description?: string;
}): string {
  const description = request.description ?? '';
  if (request.tool_name !== SEND_SLACK_MESSAGE_ACTION) return description;
  return description.split('\n')[0].replace(/:\s*$/, '.');
}

/**
 * The Slack message a send card asks to post, from the card's own arguments
 * (what the server stored and posts on Confirm): the channel or member it
 * goes to, whether the channel is shared with another company, then the
 * exact text. Null for any other card.
 */
export function SendSlackMessagePreview({
  request,
}: {
  request: ConfirmationRequest;
}) {
  if (request.tool_name !== SEND_SLACK_MESSAGE_ACTION) return null;
  const args = request.arguments ?? {};
  const channel = text(args.channel_name) || text(args.channel_id);
  const destination = channel
    ? `#${channel.replace(/^#/, '')}`
    : text(args.member_name);
  const shared = Boolean(channel) && args.externally_shared === true;
  const body = typeof args.text === 'string' ? args.text : '';
  return (
    <div
      className='overflow-hidden rounded-[8px] border border-border bg-background text-[12px] leading-[16px]'
      data-testid='send-slack-message-preview'
    >
      <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-[12px] border-b border-border px-[10px] py-[8px]'>
        <dt className='text-muted-foreground'>To</dt>
        <dd className='flex min-w-0 flex-wrap items-center gap-[6px] font-medium break-words text-foreground'>
          <span className='min-w-0 break-words'>{destination}</span>
          {shared ? (
            <Badge variant='warning'>Shared with another company</Badge>
          ) : null}
        </dd>
      </dl>
      {body ? (
        <p className='max-h-[240px] overflow-y-auto px-[10px] py-[8px] break-words whitespace-pre-wrap text-foreground'>
          {body}
        </p>
      ) : null}
    </div>
  );
}
